import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, Lock, Eye, EyeOff, Coins, Zap, Shield } from 'lucide-react';

const Login: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Handle login logic here
    console.log('Login attempt:', { email, password, rememberMe });
  };

  return (
    <div className="min-h-screen bg-win-dark-bg flex">
      {/* Left Side - Login Form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          {/* Logo */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-3 mb-4">
              <div className="w-16 h-16 bg-gradient-to-br from-win-dark-gold to-win-dark-gold-dark rounded-3xl flex items-center justify-center win-glow-gold">
                <Coins className="w-8 h-8 text-win-dark-bg" />
              </div>
              <div className="text-left">
                <h1 className="text-3xl font-bold text-win-dark-text">X Gold</h1>
                <p className="text-win-dark-text-muted">AI Trading Platform</p>
              </div>
            </div>
          </div>

          {/* Welcome Text */}
          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold text-win-dark-text mb-2">Welcome Back!</h2>
            <p className="text-win-dark-text-muted">
              Sign in to your account to continue trading gold with AI
            </p>
          </div>

          {/* Login Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Email Input */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-win-dark-text mb-2">
                Email Address
              </label>
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-win-dark-text-muted" />
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email"
                  className="win-input w-full pl-12"
                  required
                />
              </div>
            </div>

            {/* Password Input */}
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-win-dark-text mb-2">
                Password
              </label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-win-dark-text-muted" />
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  className="win-input w-full pl-12 pr-12"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 transform -translate-y-1/2 text-win-dark-text-muted hover:text-win-dark-text"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            {/* Remember Me & Forgot Password */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <input
                  id="remember"
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  className="w-4 h-4 rounded border-win-dark-border bg-win-dark-surface focus:ring-win-dark-accent focus:ring-2"
                />
                <label htmlFor="remember" className="text-sm text-win-dark-text-muted">
                  Remember me
                </label>
              </div>
              <Link
                to="/forgot-password"
                className="text-sm text-win-dark-accent hover:text-win-dark-accent-hover font-medium"
              >
                Forgot password?
              </Link>
            </div>

            {/* Login Button */}
            <button
              type="submit"
              className="w-full win-button py-4 text-lg font-bold"
            >
              Sign In
            </button>
          </form>

          {/* Demo Account */}
          <div className="mt-6">
            <button className="w-full win-button-secondary py-3 flex items-center justify-center gap-2">
              <Zap className="w-5 h-5" />
              <span>Try Demo Account</span>
            </button>
          </div>

          {/* Sign Up Link */}
          <div className="text-center mt-8">
            <p className="text-win-dark-text-muted">
              Don't have an account?{' '}
              <Link
                to="/register"
                className="text-win-dark-accent hover:text-win-dark-accent-hover font-medium"
              >
                Sign up for free
              </Link>
            </p>
          </div>

          {/* Security Notice */}
          <div className="mt-8 p-4 bg-win-dark-surface rounded-3xl border border-win-dark-border">
            <div className="flex items-start gap-3">
              <Shield className="w-5 h-5 text-win-dark-accent mt-0.5" />
              <div>
                <p className="text-sm font-medium text-win-dark-text mb-1">Secure & Protected</p>
                <p className="text-xs text-win-dark-text-muted">
                  Your data is encrypted and protected with bank-level security.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Right Side - Hero Section */}
      <div className="hidden lg:flex flex-1 bg-gradient-to-br from-win-dark-surface to-win-dark-surface-light p-8 items-center justify-center">
        <div className="max-w-lg text-center">
          <div className="mb-8">
            <div className="w-32 h-32 bg-gradient-to-br from-win-dark-gold to-win-dark-gold-dark rounded-full mx-auto mb-6 flex items-center justify-center win-glow-gold">
              <Coins className="w-16 h-16 text-win-dark-bg" />
            </div>
            <h3 className="text-3xl font-bold text-win-dark-text mb-4">
              Trade Gold with AI Power
            </h3>
            <p className="text-lg text-win-dark-text-muted mb-8">
              Join thousands of traders using our advanced AI algorithms to maximize their gold trading profits.
            </p>
          </div>

          {/* Features */}
          <div className="space-y-4">
            <div className="flex items-center gap-4 p-4 bg-win-dark-bg/50 rounded-3xl">
              <div className="p-2 bg-win-dark-accent/20 rounded-2xl">
                <Zap className="w-6 h-6 text-win-dark-accent" />
              </div>
              <div className="text-left">
                <p className="font-medium text-win-dark-text">AI-Powered Trading</p>
                <p className="text-sm text-win-dark-text-muted">Advanced algorithms for optimal trades</p>
              </div>
            </div>

            <div className="flex items-center gap-4 p-4 bg-win-dark-bg/50 rounded-3xl">
              <div className="p-2 bg-win-dark-success/20 rounded-2xl">
                <Shield className="w-6 h-6 text-green-400" />
              </div>
              <div className="text-left">
                <p className="font-medium text-win-dark-text">Secure Trading</p>
                <p className="text-sm text-win-dark-text-muted">Bank-level security and encryption</p>
              </div>
            </div>

            <div className="flex items-center gap-4 p-4 bg-win-dark-bg/50 rounded-3xl">
              <div className="p-2 bg-win-dark-gold/20 rounded-2xl">
                <Coins className="w-6 h-6 text-win-dark-gold" />
              </div>
              <div className="text-left">
                <p className="font-medium text-win-dark-text">Real-time Markets</p>
                <p className="text-sm text-win-dark-text-muted">Live gold prices and instant execution</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;